import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export type AppFlow = 'smart-dine-in' | 'reserve-table' | 'order-online'

export function useAppFlow() {
  const route = useRoute()
  const router = useRouter()

  const explicitFlow = computed<AppFlow | null>(() => {
    const flowParam = route.query.flow as AppFlow
    if (flowParam && ['smart-dine-in', 'reserve-table', 'order-online'].includes(flowParam)) {
      return flowParam
    }

    return null
  })

  const contextualFlow = computed<AppFlow | null>(() => {
    if (explicitFlow.value) {
      return explicitFlow.value
    }

    // Detect flow from route context
    // Smart Dine-in: has table_no query param (QR code or reservation link)
    if (route.query.table_no) {
      return 'smart-dine-in'
    }

    if (route.name === 'SmartDineInHome') {
      return 'smart-dine-in'
    }

    // Reserve Table: dedicated reservation flow screens
    if (route.path === '/reservations' || route.path.startsWith('/reservations/')) {
      return 'reserve-table'
    }

    // Order online: dedicated order flow screens
    if (route.name === 'Restaurants' || route.name === 'RestaurantMenu' || route.name === 'Cart') {
      return 'order-online'
    }

    return null
  })

  const isFlowContext = computed(() => contextualFlow.value !== null)

  const currentFlow = computed<AppFlow>(() => {
    if (contextualFlow.value) {
      return contextualFlow.value
    }

    return 'order-online'
  })

  // Helper: Check if this is a reservation link flow (requires PIN validation)
  const isReservationLinkFlow = computed(() => {
    return (
      route.query.token_validate === 'true' && !!route.query.table_no && !route.query.reservation_id
    )
  })

  // Helper: Check if this is a QR code flow (no PIN required, login on order)
  const isQrCodeFlow = computed(() => {
    return !!route.query.table_no && route.query.token_validate !== 'true'
  })

  // Helper: Check if reservation is validated (has reservation_id)
  const hasValidatedReservation = computed(() => {
    return !!route.query.reservation_id
  })

  // Helper: Check if reservation validation is needed
  const needsReservationValidation = computed(() => {
    return (
      route.query.token_validate === 'true' && !!route.query.table_no && !route.query.reservation_id
    )
  })

  const setFlow = (flow: AppFlow, path?: string) => {
    const targetPath = path || route.path
    router.push({
      path: targetPath,
      query: { ...route.query, flow },
    })
  }

  const getFlowRoute = (baseRoute: string, flow?: AppFlow): string => {
    const targetFlow = flow || currentFlow.value
    const separator = baseRoute.includes('?') ? '&' : '?'
    return `${baseRoute}${separator}flow=${targetFlow}`
  }

  const getContextualRoute = (baseRoute: string, flow?: AppFlow | null): string => {
    const targetFlow = flow === undefined ? contextualFlow.value : flow

    if (!targetFlow) {
      return baseRoute
    }

    const separator = baseRoute.includes('?') ? '&' : '?'
    return `${baseRoute}${separator}flow=${targetFlow}`
  }

  // Helper: Get smart-dine-in route with proper query params
  const getSmartDineInRoute = (
    tenantId: string,
    options?: {
      tableNo?: string
      tokenValidate?: boolean
      reservationId?: string
    }
  ): string => {
    const baseRoute = `/restaurant-menu-view/${tenantId}`
    const queryParams: Record<string, string> = {
      flow: 'smart-dine-in',
    }

    if (options?.tableNo) {
      queryParams.table_no = options.tableNo
    }

    if (options?.tokenValidate) {
      queryParams.token_validate = 'true'
    }

    if (options?.reservationId) {
      queryParams.reservation_id = options.reservationId
    }

    const queryString = new URLSearchParams(queryParams).toString()
    return `${baseRoute}?${queryString}`
  }

  return {
    explicitFlow,
    contextualFlow,
    isFlowContext,
    currentFlow,
    isReservationLinkFlow,
    isQrCodeFlow,
    hasValidatedReservation,
    needsReservationValidation,
    setFlow,
    getFlowRoute,
    getContextualRoute,
    getSmartDineInRoute,
  }
}
